/*
 * fn: getComboBracketInfo
 *
 * Determines and returns combo bracket info data
 *
 */
import { type ComboBaseType, type ComboBracketType } from '@/defs/combo'
import { GAME_CHANGERS } from '@/defs/mtg'
import { parseCardId } from '@/utils/cardUtil'

import { getComboContested } from './getComboContested'

type BracketProps = {
  combo: ComboBaseType
  contested: ReturnType<typeof getComboContested>
  gameChangerCount: number
}

type BracketMidProps = BracketProps & { low: ComboBracketType }

const getLowBracketInfo = ({ combo, contested, gameChangerCount }: BracketProps): ComboBracketType => {
  const { isContestedPrereq, isContestedResult } = contested

  if (gameChangerCount) {
    return {
      message: 'This combo requires one or more Game Changers, which are not allowed in Brackets 1 and 2.',
      status: 'invalid',
    }
  }

  if (combo.cards.length < 2) {
    return {
      message: 'This combo came back malformed, please refresh.',
      status: 'invalid',
    }
  }

  if (combo.cards.length !== 2) {
    return {
      message: 'Any combos that require three or more cards are allowed.',
      status: 'valid',
    }
  }

  if (!combo.vote) {
    return {
      message: `No early game, 2-card infinite combos, but the community hasn't weighed in yet if this one counts.`,
      status: 'warning',
    }
  }

  if (isContestedPrereq && (combo.vote.resultScore || 0) >= 0.5) {
    return {
      message: `The community has determined this isn't a true 2-card combo because the prerequisites are nontrivial. However, it was a close vote. Play with caution.`,
      status: 'warning',
    }
  }

  if (isContestedResult && (combo.vote.prereqScore || 0) >= 0.5) {
    return {
      message: `The community has determined this isn't a true 2-card combo because the results are not significant. However, it was a close vote. Play with caution.`,
      status: 'warning',
    }
  }

  if (isContestedPrereq && isContestedResult) {
    return {
      message: `The community has determined this isn't a true 2-card combo because the prerequisites are nontrivial and the results are not significant. However, it was a close vote. Play with caution.`,
      status: 'warning',
    }
  }

  if (combo.vote.bracket === 'any') {
    return {
      message: `The community has determined this isn't a true 2-card combo.`,
      status: 'valid',
    }
  }

  return {
    message: `The community has determined this is a true 2-card combo.`,
    status: 'invalid',
  }
}

const getMidBracketInfo = ({ combo, contested, gameChangerCount, low }: BracketMidProps): ComboBracketType => {
  const { isContestedEarly } = contested

  if (gameChangerCount > 3) {
    return {
      message: `This combo requires over three Game Changers. You may have a maximum of three Game Changers in Bracket 3.`,
      status: 'invalid',
    }
  }

  if (combo.cards.length < 2) {
    return {
      message: 'This combo came back malformed, please refresh.',
      status: 'invalid',
    }
  }

  if (combo.cards.length !== 2) {
    return {
      message: `Any combos that require three or more cards are allowed.`,
      status: 'valid',
    }
  }

  if (!combo.vote) {
    return {
      message: `No early game, 2-card infinite combos, but the community hasn't weighed in yet if this one counts.`,
      status: 'warning',
    }
  }

  if (low.status === 'warning') {
    if ((combo.vote.earlyScore || 0) < 0.4) {
      return {
        message: `The community has determined this isn't a true 2-card combo in a close vote. However, it was clear that it's not early game, so it's safe regardless.`,
        status: 'valid',
      }
    }

    if (isContestedEarly) {
      return {
        message: `The community has determined this isn't a true 2-card combo in a close vote and that it's not early game in a close vote. Play with caution.`,
        status: 'warning',
      }
    }

    return {
      message: `The community has determined this isn't a true 2-card combo in a close vote while it is an early game combo. Play with caution.`,
      status: 'warning',
    }
  }

  if (isContestedEarly && low.status === 'valid') {
    return {
      message: `The community has determined this isn't an early game combo. However, it was a close vote. Play with caution.`,
      status: 'warning',
    }
  }

  if (combo.vote.bracket === 'any') {
    return {
      message: `The community has determined this isn't a true 2-card combo.`,
      status: 'valid',
    }
  }

  if (combo.vote.bracket === '3') {
    return {
      message: `The community has determined this isn't an early game combo.`,
      status: 'valid',
    }
  }

  return {
    message: `The community has determined this is a true, early game, 2-card combo.`,
    status: 'invalid',
  }
}

type Props = {
  comboBase: ComboBaseType
}

export const getComboBracketInfo = ({ comboBase }: Props) => {
  const bracketProps: BracketProps = {
    combo: comboBase,
    contested: getComboContested(comboBase),
    gameChangerCount: comboBase.cards.filter((card) => {
      const slug = parseCardId(card.cardId).slug
      return GAME_CHANGERS.includes(slug)
    }).length,
  }

  const low = getLowBracketInfo(bracketProps)
  const mid = getMidBracketInfo({ ...bracketProps, low })

  return { low, mid }
}
